import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Activity, AlertTriangle, CheckCircle2, RefreshCw, Search, Zap } from "lucide-react";
import { useAuth } from "../AuthContext";
import { apiFetch } from "../utils/api";
import { actionFailureMessage, useLongActionNotice } from "../utils/actionFeedback";
import DashboardLayout from "../components/layout/DashboardLayout";
import RecruiterSidebar from "../components/layout/RecruiterSidebar";

export default function CandidateProcesses() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [processGroups, setProcessGroups] = useState({ active: [], history: [] });
  const [fetching, setFetching] = useState(true);
  const [updatingBatchId, setUpdatingBatchId] = useState(null);
  const [historyRange, setHistoryRange] = useState("1");

  useLongActionNotice(Boolean(updatingBatchId), "Background activity update", 30000);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  const fetchSummary = async ({ silent = false } = {}) => {
    if (!silent) setFetching(true);

    try {
      const res = await apiFetch("/candidates/search-index-summary");
      const data = await res.json();

      if (!res.ok) {
        if (!silent) {
          toast.error(actionFailureMessage("Load background activity", data?.detail));
        }
        return;
      }

      setSummary(data || {});

      const processParams = new URLSearchParams({ limit: "100" });
      if (historyRange !== "all") {
        processParams.set("history_days", historyRange);
      }
      const processRes = await apiFetch(`/candidates/search-index-process-groups?${processParams.toString()}`);
      const processData = await processRes.json();

      if (processRes.ok) {
        setProcessGroups({
          active: processData.active || [],
          history: processData.history || [],
        });
      } else if (!silent) {
        toast.error(actionFailureMessage("Load background activity", processData?.detail));
      }
    } catch (err) {
      if (!silent) {
        toast.error(actionFailureMessage("Load background activity"));
      }
    } finally {
      if (!silent) setFetching(false);
    }
  };

  useEffect(() => {
    if (!user) return undefined;

    fetchSummary();
    const intervalId = window.setInterval(() => {
      fetchSummary({ silent: true });
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [user, historyRange]);

  const totals = useMemo(() => {
    const total = summary?.total || 0;
    const ready = summary?.ready || 0;
    const failed = summary?.failed || 0;
    const processing = summary?.processing || 0;
    const pending = summary?.pending || 0;
    const complete = total > 0 && ready + failed >= total;
    const progress = total ? Math.round(((ready + failed) / total) * 100) : 100;

    return { total, ready, failed, processing, pending, complete, progress };
  }, [summary]);

  const leftPanel = <RecruiterSidebar activeItem="candidate_processes" user={user} />;

  const formatDate = (value) => {
    if (!value) return "Not available";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Not available";

    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const activeGroups = processGroups.active || [];
  const historyGroups = processGroups.history || [];
  const filteredHistoryGroups = historyGroups;
  const latestActivityDate = [...activeGroups, ...historyGroups]
    .map((group) => group.latest_activity_at)
    .filter(Boolean)
    .sort((a, b) => new Date(b) - new Date(a))[0];

  const handleBatchAction = async (batchId, action) => {
    if (!batchId) return;

    if (action === "resolve" && !window.confirm("Mark failed indexing items as resolved? These candidates can still be scanned, but may not appear in AI Talent Search until they are indexed again.")) {
      return;
    }

    setUpdatingBatchId(batchId);

    try {
      const res = await apiFetch(`/candidates/import-batches/${batchId}/${action}-indexing`, {
        method: "POST",
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage(`${action} indexing`, data?.detail));
        return;
      }

      toast.success(data?.message || "Background activity updated.");
      await fetchSummary();
    } catch (err) {
      toast.error(actionFailureMessage(`${action} indexing`));
    } finally {
      setUpdatingBatchId(null);
    }
  };

  const renderProcessCard = (group, active = false) => {
    if (!active) {
      return (
        <div
          key={group.group_key}
          className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm"
        >
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(320px,.9fr)_minmax(120px,150px)] lg:items-center">
            <div className="min-w-0">
              <div className="mb-1 inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                <Search size={12} />
                {group.status_label}
              </div>
              <h2 className="truncate text-sm font-semibold text-slate-950">
                {group.context_label}
              </h2>
              <p className="mt-1 truncate text-xs text-slate-500">
                Latest: {group.latest_candidate || "Not available"}
              </p>
            </div>

            <div className="grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                { label: "Uploaded", value: group.total },
                { label: "Indexed", value: group.indexed ?? group.ready },
                { label: "Reused", value: group.duplicate_count || 0 },
                { label: "Failed", value: (group.skipped_count || 0) + (group.failed || 0) },
              ].map((item) => (
                <div key={item.label} className="min-w-0 rounded-lg bg-slate-50 px-2.5 py-2">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    {item.label}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-slate-950">{item.value}</p>
                </div>
              ))}
            </div>

            <div className="min-w-0 text-xs text-slate-500">
              <div className="mb-2 flex items-center justify-between gap-3">
                <span>{formatDate(group.latest_activity_at) || group.import_date}</span>
                <span className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-700">
                  {group.progress}%
                </span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-emerald-600"
                  style={{ width: `${group.progress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
    <div
      key={group.group_key}
      className={`overflow-hidden rounded-lg border p-4 shadow-sm ${
        active
          ? "border-emerald-200 bg-gradient-to-br from-emerald-950 via-emerald-900 to-slate-950 text-white"
          : "border-slate-200 bg-white text-slate-900"
      }`}
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${
            active
              ? "border-emerald-300/35 bg-emerald-400/15 text-emerald-100"
              : "border-slate-200 bg-slate-50 text-slate-500"
          }`}>
            <Search size={14} className={active ? "animate-pulse" : ""} />
            {group.status_label}
          </div>
          <h2 className="mt-3 truncate text-lg font-semibold">
            {group.context_label}
          </h2>
          <p className={`mt-1.5 text-sm leading-5 ${active ? "text-emerald-50/75" : "text-slate-500"}`}>
            {group.ready} / {group.total} uploaded files processed from {group.source}.
          </p>
        </div>

        <div className={`rounded-lg border px-3 py-2 text-sm font-semibold ${
          active
            ? "border-white/15 bg-white/10 text-emerald-50"
            : "border-slate-200 bg-slate-50 text-slate-700"
        }`}>
          {group.progress}%
        </div>
      </div>

      <div className={`mt-4 h-2 overflow-hidden rounded-full ${active ? "bg-white/15" : "bg-slate-100"}`}>
        <div
          className={`h-full rounded-full transition-all duration-700 ${
            active
              ? "bg-gradient-to-r from-emerald-300 via-lime-200 to-emerald-100"
              : "bg-emerald-600"
          }`}
          style={{ width: `${group.progress}%` }}
        />
      </div>

      <div className="mt-4 grid gap-2 sm:grid-cols-4">
        {[
          { label: "Uploaded", value: group.total },
          { label: "Search indexed", value: group.indexed ?? group.ready },
          { label: "Reused", value: group.duplicate_count || 0 },
          { label: "Skipped / failed", value: (group.skipped_count || 0) + (group.failed || 0) },
        ].map((item) => (
          <div
            key={item.label}
            className={`rounded-lg border p-2.5 ${
              active
                ? "border-white/10 bg-white/10"
                : "border-slate-200 bg-slate-50"
            }`}
          >
            <p className={`text-[11px] font-semibold uppercase tracking-wide ${active ? "text-emerald-100/60" : "text-slate-400"}`}>
              {item.label}
            </p>
            <p className="mt-2 text-lg font-semibold">{item.value}</p>
          </div>
        ))}
      </div>

      <div className={`mt-3 grid gap-2 text-[11px] sm:grid-cols-3 ${active ? "text-emerald-50/75" : "text-slate-500"}`}>
        <span>Import date: {group.import_date}</span>
        <span>Latest candidate: {group.latest_candidate || "Not available"}</span>
        <span>Last activity: {formatDate(group.latest_activity_at)}</span>
      </div>

      {group.batch_id && group.failed > 0 && (
        <div className={`mt-5 flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between ${
          active
            ? "border-amber-200/25 bg-amber-100/10"
            : "border-amber-200 bg-amber-50"
        }`}>
          <p className={`text-sm leading-6 ${active ? "text-amber-50" : "text-amber-800"}`}>
            {group.failed} candidate{group.failed === 1 ? "" : "s"} need indexing attention.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={() => handleBatchAction(group.batch_id, "retry")}
              disabled={updatingBatchId === group.batch_id}
              className={`inline-flex min-h-[36px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold transition disabled:opacity-60 ${
                active
                  ? "bg-white text-emerald-950 hover:bg-emerald-50"
                  : "bg-emerald-800 text-white hover:bg-emerald-900"
              }`}
            >
              <RefreshCw size={15} className={updatingBatchId === group.batch_id ? "animate-spin" : ""} />
              Retry
            </button>
            <button
              type="button"
              onClick={() => handleBatchAction(group.batch_id, "resolve")}
              disabled={updatingBatchId === group.batch_id}
              className={`inline-flex min-h-[36px] items-center justify-center gap-2 rounded-lg border px-3 text-sm font-semibold transition disabled:opacity-60 ${
                active
                  ? "border-white/20 text-white hover:bg-white/10"
                  : "border-amber-200 bg-white text-amber-800 hover:bg-amber-100"
              }`}
            >
              <CheckCircle2 size={15} />
              Mark Resolved
            </button>
          </div>
        </div>
      )}
    </div>
    );
  };

  const mainContent = (
    <div className="space-y-6">
      {(totals.processing > 0 || totals.pending > 0) && (
        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50">
                <Search className={totals.processing > 0 ? "animate-pulse text-emerald-700" : "text-emerald-700"} size={24} />
              </div>
              <div>
                <p className="text-lg font-semibold text-slate-950">AI Talent Search indexing</p>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
                  Active imports are grouped by job and import method. Completed groups become a compact history, so recruiters can see what happened without scanning a long process list.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => fetchSummary()}
              disabled={fetching}
              className="inline-flex min-h-[38px] items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <RefreshCw size={16} className={fetching ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>

          <div className="mt-6 h-3 overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-emerald-600 transition-all duration-700"
              style={{ width: `${totals.progress}%` }}
            />
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-5">
            {[
              { label: "Total candidates", value: totals.total, icon: Activity },
              { label: "Search indexed", value: totals.ready, icon: CheckCircle2 },
              { label: "Processing", value: totals.processing, icon: Zap },
              { label: "Queued", value: totals.pending, icon: Activity },
              { label: "Needs attention", value: totals.failed, icon: AlertTriangle },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.label} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{item.label}</p>
                    <Icon size={17} className="text-emerald-700" />
                  </div>
                  <p className="mt-3 text-2xl font-semibold text-slate-950">{item.value}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="space-y-4">
        {activeGroups.length > 0 ? (
          activeGroups.map((group) => renderProcessCard(group, true))
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                <CheckCircle2 size={20} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-950">No active background indexing</h2>
                <p className="mt-1 text-sm leading-6 text-slate-500">
                  Candidate search indexing is idle. Latest activity: {formatDate(latestActivityDate)}.
                </p>
              </div>
            </div>
          </div>
        )}
      </section>

      {historyGroups.length > 0 && (
        <section className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-950">Import History</h2>
              <p className="mt-1 text-sm leading-6 text-slate-500">
                Completed candidate imports grouped by job, method, and date.
              </p>
            </div>
            <label className="flex flex-col gap-1 text-sm font-semibold text-slate-700">
              Date range
              <select
                value={historyRange}
                onChange={(event) => setHistoryRange(event.target.value)}
                className="min-w-[180px] rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              >
                <option value="1">Last 24 hours</option>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="all">All history</option>
              </select>
            </label>
          </div>
          {filteredHistoryGroups.length > 0 ? (
            <div className="grid gap-4 xl:grid-cols-2">
              {filteredHistoryGroups.map((group) => renderProcessCard(group, false))}
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
              No completed imports found for this date range.
            </div>
          )}
        </section>
      )}

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <div className={`mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
            totals.failed > 0
              ? "bg-amber-100 text-amber-700"
              : totals.processing > 0 || totals.pending > 0
              ? "bg-sky-100 text-sky-700"
              : "bg-emerald-100 text-emerald-700"
          }`}>
            {totals.failed > 0 ? <AlertTriangle size={18} /> : totals.complete ? <CheckCircle2 size={18} /> : <Activity size={18} />}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-950">
              {totals.failed > 0
                ? "Some candidates need attention"
                : totals.processing > 0
                ? "Background work is still running"
                : totals.pending > 0
                ? "Some candidates are queued for search indexing"
                : "No active background tasks"}
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
              {totals.failed > 0
                ? "A few candidate records could not be indexed for AI Talent Search. They can still be reviewed and scanned if their CV text was imported successfully."
                : totals.processing > 0
                ? "Recruiters can continue importing candidates, opening jobs, and scanning applicants while search indexing finishes."
                : totals.pending > 0
                ? "These candidates are waiting for AI Talent Search indexing. They are still available for scanning if their CV text was imported successfully."
                : "Candidate search indexing is up to date for the current candidate library."}
            </p>
          </div>
        </div>
      </section>
    </div>
  );

  if (loading || !user) return null;

  return (
    <DashboardLayout
      user={user}
      title="Background Activity"
      eyebrow="Candidate processing"
      subtitle="Monitor candidate search indexing and other background preparation work without leaving the recruiter flow."
      leftPanel={leftPanel}
      mainContent={mainContent}
    />
  );
}
